/**
 * The presenter's choice for the classic (WebRTC) connection over MoQ, remembered per browser.
 * Only the switch in the device dialog writes it: a fallback on one page says nothing about the
 * next broadcast, so it is never stored.
 */
const PREFER_CLASSIC_KEY = 'onlinewebinar.broadcast.preferClassicConnection';

interface PreferenceStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

// Reading localStorage itself can throw (blocked site data), not only its methods.
const browserStorage = (): PreferenceStorage | undefined => {
    try {
        return globalThis.localStorage ?? undefined;
    } catch {
        return undefined;
    }
};

export const readPreferClassicConnection = (storage: PreferenceStorage | undefined = browserStorage()): boolean => {
    try {
        return storage?.getItem(PREFER_CLASSIC_KEY) === '1';
    } catch {
        return false;
    }
};

export const writePreferClassicConnection = (prefer: boolean, storage: PreferenceStorage | undefined = browserStorage()): void => {
    try {
        if (prefer) storage?.setItem(PREFER_CLASSIC_KEY, '1');
        else storage?.removeItem(PREFER_CLASSIC_KEY);
    } catch {
        // Not remembered; the default applies next time.
    }
};

/**
 * On since MoQ publishes AAC (moqAudioConfig): with Opus the recording and live subtitles had no sound, as the
 * RTMP replay they read cannot carry Opus. Turning this off makes every presenter use the classic connection
 * and hides the switch that opts out of MoQ, since its text describes MoQ as the default.
 */
export const MOQ_IS_DEFAULT = true;

/** MoQ is the default where it works (Chromium), unless this browser asked for the classic connection. */
export const defaultMoqPublisher = (chromium: boolean, preferClassic: boolean, moqIsDefault: boolean = MOQ_IS_DEFAULT): boolean =>
    moqIsDefault && chromium && !preferClassic;
